import { useEffect, useState, useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Autocomplete from "@/components/Autocomplete";
import Icon from "@/components/Icon";
import { GET_FACILITIES } from "../../queries";
import { useQuery } from "@apollo/client";
import { Facility } from "../types";

interface FacilitiesQueryData {
  facilities: {
    nodes: Facility[];
  };
}

interface FacilityAutocompleteProps {
  sectionId: string;
  onFacilitySelect?: (facility: Facility) => void;
  required?: boolean;
}

const arrayWrap = (x: string) => (!!x && [x]) || [];

const FacilityAutocomplete = ({ sectionId, onFacilitySelect, required = false }: FacilityAutocompleteProps) => {
  const {
    control,
    setValue,
    trigger,
    clearErrors,
    watch,
    formState: { errors }
  } = useFormContext();

  const facility = watch("facility");
  const facilityId = watch("facilityId");
  const facilityName = watch("facilityName");

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [initialFacilityLoaded, setInitialFacilityLoaded] = useState(false);

  const [variables, setVariables] = useState({
    input: {
      sectionIds: arrayWrap(sectionId),
      search: facility || ""
    }
  });

  const { data: facilityData } = useQuery<FacilitiesQueryData>(GET_FACILITIES, {
    variables: watch("section") === "National Section" ? { input: { search: variables.input.search } } : variables,
    fetchPolicy: "network-only"
  });

  const selectFacility = useCallback(
    (value: any, setValue: any) => {
      setValue("facilityId", value.id);
      setValue("facility", value.name);
      onFacilitySelect?.(value);
      clearErrors(["facility", "facilityId"]);
    },
    [onFacilitySelect, clearErrors]
  );

  // Handle initial facility data loading
  useEffect(() => {
    if (isInitialLoad && facilityId && facilityName && !initialFacilityLoaded) {
      // Set the facility name immediately
      setValue("facility", facilityName);

      // If we have facility data in the query results, select it
      const matchingFacility = facilityData?.facilities?.nodes?.find((f) => f.id === facilityId);
      if (matchingFacility) {
        selectFacility(matchingFacility, setValue);
        setInitialFacilityLoaded(true);
      }
    }
  }, [facilityId, facilityName, facilityData, isInitialLoad, initialFacilityLoaded, setValue, selectFacility]);

  // Reset initial load state when facility changes
  useEffect(() => {
    if (facility && !isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [facility, isInitialLoad]);

  // Update search variables when facility or section changes
  useEffect(() => {
    if (!isInitialLoad) {
      setVariables({
        input: {
          sectionIds: arrayWrap(sectionId),
          search: facility || ""
        }
      });
    }
  }, [facility, sectionId, isInitialLoad]);

  return (
    <>
      <Controller
        name="facility"
        control={control}
        rules={required ? { required: "Facility is required" } : undefined}
        render={({ field: { onChange, onBlur, value } }) => {
          return (
            <Autocomplete
              label="Facility"
              name={"facilityId"}
              query={value}
              required={required}
              data={facilityData?.facilities?.nodes || []}
              onChangeText={(value) => {
                onChange(value);
                setValue("facility", value);
                setValue("facilityId", "");
                setIsInitialLoad(false);
                if (value && errors.facility) {
                  clearErrors("facility");
                }
              }}
              onBlur={() => {
                onBlur();
                if (value && !facilityId) {
                  setValue("facility", "");
                  trigger("facility");
                }
              }}
              onMouseDown={(e: MouseEvent) => {
                e.preventDefault();
              }}
              onSelect={(value) => {
                selectFacility(value, setValue);
                setIsInitialLoad(false);
              }}
              reset={() => {
                setValue("facility", "");
                setValue("facilityId", "");
                setIsInitialLoad(false);
              }}
              error={!!errors?.facility || !!errors?.facilityId}
              errorText={(errors?.facility?.message || errors?.facilityId?.message) as string}
              renderItem={(data) => (
                <div className="flex items-start border-bottom">
                  <div className="bg-sys-brand-secondary-container rounded-full p-2 mr-4 flex items-center justify-center">
                    <Icon name="location_on" size={24} />
                  </div>
                  <div>
                    <div className="text-body-large">{data.name}</div>
                    {data.addresses[0] && (
                      <div className="text-body-medium text-sys-brand-on-surface-varian">
                        {data.addresses[0]?.address1}, {data.addresses[0]?.city}, {data.addresses[0]?.state}
                      </div>
                    )}
                  </div>
                </div>
              )}
            />
          );
        }}
      />
    </>
  );
};

export default FacilityAutocomplete;
